import React from "react";
import { toast as sonnerToast } from "sonner";
import {
	IconStatusError,
	IconStatusSuccess,
	IconStatusWarning,
} from "@jasperlabs/jux-next";

const GENERIC_ERROR_MESSAGE =
	"Something went wrong and we have recorded this incident. We're working to fix it, so please try again later.";

type ToastProps = {
	message: React.ReactNode;
	variant?: "info" | "warning" | "success" | "error";
};

function Toast({ message, variant }: ToastProps) {
	return (
		<div className="flex rounded-lg bg-white shadow-lg ring-1 ring-black/5 w-full items-center p-4 gap-4">
			<div>
				{variant === "warning" && <IconStatusWarning />}
				{variant === "success" && <IconStatusSuccess />}
				{variant === "error" && <IconStatusError />}
			</div>
			<p className="w-full text-base text-color-body">{message}</p>
		</div>
	);
}

/**
 * Functions for displaying toasts
 */
export const toast = {
	/**
	 * Displays a toast with a warning icon and the given message
	 */
	warning: (message: React.ReactNode) => {
		sonnerToast.custom(() => <Toast message={message} variant="warning" />);
	},

	/**
	 * Displays a toast with a success icon and the given message
	 */
	success: (message: React.ReactNode) => {
		sonnerToast.custom(() => <Toast message={message} variant="success" />);
	},

	/**
	 * Displays a toast with an error icon and the given message
	 */
	error: (message: React.ReactNode) => {
		sonnerToast.custom(() => <Toast message={message} variant="error" />);
	},

	/**
	 * Displays a toast with a warning icon and a generic error message:
	 *
	 * "Something went wrong and we have recorded this incident. We're working to fix it, so please try again later."
	 */
	errorGeneric: () => {
		sonnerToast.custom(() => (
			<Toast message={GENERIC_ERROR_MESSAGE} variant="warning" />
		));
	},

	dismiss: sonnerToast.dismiss,
};
